import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import { Box, IconButton, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { CellWithSecondaryText } from "components/DataGrid";
import { IssueCount } from "components/IssueCount";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getChemicalsBreadcrumb, getChemicalsContactsBreadcrumb } from "util/breadcrumb";

import { gql } from "../../../../generated-graphql";
import { ContactsFacilitiesQuery } from "../../../../generated-graphql/graphql";
import { useBreadcrumb } from "../../../../hooks/useBreadcrumbs";

type Row = ContactsFacilitiesQuery["chemicalEnabledFacilities"]["items"][0];

const CONTACTS_FACILITIES = gql(`
  query ContactsFacilities($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    chemicalEnabledFacilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        customerFacilityId
        collectionMode
        streetAddress1
        city
        state
        issues {
          key
          modelName
        }
      }
      count
    }
  }
`);

export function ContactsFacilitiesTable() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useBreadcrumb([
    {
      label: "Chemicals",
      to: tenantId ? getChemicalsBreadcrumb(tenantId) : undefined,
    },
    {
      label: "Contacts",
      to: tenantId ? getChemicalsContactsBreadcrumb(tenantId) : undefined,
    },
  ]);

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        field: "facility",
        headerName: "Facility",
        filterKeyType: "facility",
        flex: 0.5,
        renderCell: (params) => (
          <CellWithSecondaryText
            primaryValue={
              params.row.customerFacilityId
                ? `${params.row.name} (${params.row.customerFacilityId})`
                : params.row.name
            }
            secondaryValue={`${
              params.row.streetAddress1 ? params.row.streetAddress1 : ""
            }, ${params.row.city ? `${params.row.city}, ` : ""}${
              params.row.state ? `${params.row.state}` : ""
            }`}
          />
        ),
      },

      {
        field: "state",
        headerName: "State/Territory",
        flex: 0.25,
      },
      {
        field: "issues",
        headerName: "Contact Issues",
        flex: 0.3,
        renderCell: ({ row: { issues } }) => {
          const contactIssues = issues.filter(
            (i) => i.key === "contacts" || i.modelName === "Person"
          );
          return <IssueCount issueCount={contactIssues.length} />;
        },
        sortable: false,
      },
      {
        field: "actions",
        type: "actions",
        flex: 0.2,
        getActions: (params) => [
          <Tooltip title="Show Contacts" key={1}>
            <IconButton
              size="small"
              href={`${location.pathname}/${params.row.id}`}
              aria-label="Show Contacts"
            >
              <ArrowCircleRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>,
        ],
      },
    ],
    []
  );

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="contacts-facilities"
        isURLDriven
        columns={columns}
        dataQuery={CONTACTS_FACILITIES}
        defaultSearch={`tenantId:${tenantId}`}
        getItems={(data) => data.chemicalEnabledFacilities.items}
        getCount={(data) => data.chemicalEnabledFacilities.count}
        columnVisibilityModel={{
          ...(isMobile && {
            issues: false,
          }),
        }}
        initialSortModel={[{ field: "facility", sort: "asc" }]}
        noDataMessage="No facilities have been added to this organization yet."
        onRowClick={({ row }) => navigate(row.id)}
        getRowLink={(row) => `./${row.id}`}
        rowHeight="auto"
      />
    </Box>
  );
}
